/**
 * <product-tabs> — the PDP content sections.
 *
 * Two presentations of the SAME panels, not two components:
 *   >= 750px  a tab strip (role="tablist"/"tab"/"tabpanel"), one panel open
 *   <  750px  a stack of collapsible rows (Figma 2444:7863), all closed to start
 *
 * sections/product-tabs.liquid renders one set of panels and two sets of
 * controls — the tab buttons in the nav, and an accordion header before each
 * panel. Duplicating the panels instead would have put a second Judge.me widget
 * and a second copy of every embedded third-party script on the page.
 *
 * ROLES ARE SWAPPED, NOT LEFT LYING AROUND. An accordion header carrying
 * role="tab", or a panel still claiming role="tabpanel" while its tablist is
 * display:none, describes a widget that is not on the page. Whichever control
 * set is inactive is hidden with the `hidden` attribute so it leaves the
 * accessibility tree entirely, and the panels are relabelled to point at the
 * controls that are actually visible.
 *
 * The accordion is exclusive — opening one closes the others. That keeps a
 * single notion of "which section is open" across the breakpoint, so resizing
 * mid-session cannot land in a state the other presentation has no way to
 * express, and it matches how the same content behaves on desktop.
 *
 * Two opt-in attributes on the root (both used by the training page):
 *   data-accordion-open-first  the accordion starts with its first row open
 *                              instead of all closed (Figma 2936:12486)
 *   data-mobile-tabs           never switch to the accordion; the tab strip
 *                              stays at every width (Figma 2950:12797)
 *
 * VARIANT-FOLLOWING SPECIFICATIONS. The Specifications panel renders every
 * variant's table rows (snippets/pdp-spec-table.liquid), grouped, each group
 * tagged data-variant-ids="111 222". This element listens to the theme's
 * PUB_SUB_EVENTS.variantChange (published by assets/product-info.js with
 * event.data.variant, the new variant object) and shows the matching group of
 * every table, hiding a table (title included) when the variant has no rows. If
 * that leaves the panel with no table and no downloads, the Specifications tab,
 * its accordion header and its panel are taken out (see `unavailable`), and the
 * first remaining tab is activated if it was the open one. Per-variant output is
 * the only thing hidden; the shared panel markup is never rebuilt.
 *
 * Progressive enhancement: the headers ship `hidden` and the nav ships visible,
 * so with no JS the tab strip keeps working at every width. The nav is hidden
 * here rather than in CSS for that reason — a CSS-only hide would leave a
 * no-JS mobile visitor with no controls at all.
 */
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        // Scoped, not a subtree walk: a panel's own content can contain
        // role="tab" (Judge.me's Reviews/Questions strip, embedded widgets in
        // the stub panels). select() pairs tabs[i] with panels[i] strictly by
        // index, so one stray node would desync every tab on the page.
        this.tabs = Array.from(this.querySelectorAll(':scope > .pdp-tabs__nav [role="tab"]'));
        this.panels = Array.from(this.querySelectorAll(':scope > .pdp-tabs__panels > [role="tabpanel"]'));
        this.accordions = Array.from(this.querySelectorAll(':scope > .pdp-tabs__panels > .pdp-tabs__acc'));
        this.nav = this.querySelector(':scope > .pdp-tabs__nav');

        if (this.tabs.length === 0) return;

        this.onTabClick = this.onTabClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onAccordionClick = this.onAccordionClick.bind(this);
        this.onModeChange = this.onModeChange.bind(this);

        // Indexes of tabs taken out because their panel has nothing to show for
        // the selected variant (only the variant-following Specifications panel).
        this.unavailable = new Set();

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onKeyDown);
        });

        // No arrow-key handling on the headers: an accordion is a list of
        // independent buttons, and <button> already answers Enter and Space.
        this.accordions.forEach((header) => {
          header.addEventListener('click', this.onAccordionClick);
        });

        // Theme editor: selecting a block scrolls to an element that may be
        // display:none, so without this a merchant cannot preview or configure
        // any tab but the first. Dawn does the same in its slider sections.
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        // Deep-link support: /products/handle#reviews opens that tab. Guard the
        // empty case — location.hash.slice(1) is '' with no hash, which would
        // match any tab whose handle is also ''.
        const hash = window.location.hash.slice(1);
        const fromHash = hash ? this.tabs.findIndex((tab) => tab.dataset.tabHandle === hash) : -1;

        // Which section the tab strip shows. Always a real index.
        this.activeIndex = fromHash > -1 ? fromHash : 0;
        // Which section the accordion has open. -1 means all closed, which is
        // the design's resting state (Figma shows every row collapsed) — so a
        // deep link opens one, and a plain visit opens none.
        this.openIndex = fromHash > -1 || !this.hasAttribute('data-accordion-open-first') ? fromHash : 0;

        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.mediaQuery.addEventListener('change', this.onModeChange);
        this.applyMode();

        this.followVariant();
      }

      disconnectedCallback() {
        if (typeof this.variantUnsubscriber === 'function') this.variantUnsubscriber();
        this.variantUnsubscriber = undefined;
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
          tab.removeEventListener('keydown', this.onKeyDown);
        });
        this.accordions.forEach((header) => {
          header.removeEventListener('click', this.onAccordionClick);
        });
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.mediaQuery?.removeEventListener('change', this.onModeChange);
      }

      /**
       * Wires the Specifications tables to the selected variant. The initial
       * variant is already rendered visible by the server, so this only syncs
       * the tab with it, then subscribes for changes. pubsub.js and constants.js
       * are deferred in layout/theme.liquid ahead of this file; guarded anyway.
       */
      followVariant() {
        this.specs = this.querySelector('.pdp-tab-specs[data-spec-follow]');
        if (!this.specs) return;
        this.specsIndex = this.panels.findIndex((panel) => panel.contains(this.specs));
        if (this.specsIndex < 0) return;

        this.syncSpecsTab();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const id = event?.data?.variant?.id;
            if (id !== undefined && id !== null) this.showVariantSpecs(String(id));
          });
        }
      }

      /** @param {string} variantId */
      showVariantSpecs(variantId) {
        if (!this.specs) return;
        this.specs.querySelectorAll('[data-variant-spec-table]').forEach((table) => {
          let found = false;
          table.querySelectorAll('[data-variant-ids]').forEach((group) => {
            const match = (group.dataset.variantIds || '').split(' ').includes(variantId);
            group.hidden = !match;
            if (match) found = true;
          });
          table.hidden = !found;
        });
        this.syncSpecsTab();
      }

      /** Hides or restores the Specifications tab depending on whether its panel has content. */
      syncSpecsTab() {
        const hasContent = Boolean(this.specs.querySelector('.pdp-spec-table:not([hidden])'));
        this.setTabAvailable(this.specsIndex, hasContent);
      }

      /**
       * @param {number} index
       * @param {boolean} available
       */
      setTabAvailable(index, available) {
        if (this.unavailable.has(index) === !available) return;
        if (available) {
          this.unavailable.delete(index);
        } else {
          this.unavailable.add(index);
          if (this.activeIndex === index) this.activeIndex = this.firstAvailableIndex();
          if (this.openIndex === index) this.openIndex = -1;
        }
        this.applyMode();
      }

      /** @returns {number} the first tab not taken out, or -1 when there is none */
      firstAvailableIndex() {
        return this.tabs.findIndex((tab, i) => !this.unavailable.has(i));
      }

      onModeChange() {
        this.applyMode();
      }

      applyMode() {
        if (this.mediaQuery.matches && !this.hasAttribute('data-mobile-tabs')) {
          this.renderAccordion();
        } else {
          this.renderTabs();
        }
      }

      /** Tab strip: nav visible, headers out of the tree, one panel open. */
      renderTabs() {
        this.accordions.forEach((header) => {
          header.hidden = true;
        });
        if (this.nav) this.nav.hidden = false;
        this.tabs.forEach((tab, i) => {
          tab.hidden = this.unavailable.has(i);
        });

        this.panels.forEach((panel, i) => {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.tabs[i].id);
          // A tabpanel is the tab's own scrollable region, so it takes focus
          // when the tab is activated. A plain region does not.
          panel.setAttribute('tabindex', '0');
        });

        this.select(this.activeIndex, false);
      }

      /** Accordion: nav out of the tree, headers visible, panels independent. */
      renderAccordion() {
        if (this.nav) this.nav.hidden = true;

        this.accordions.forEach((header, i) => {
          header.hidden = this.unavailable.has(i);
          header.setAttribute('aria-expanded', i === this.openIndex ? 'true' : 'false');
        });

        this.panels.forEach((panel, i) => {
          const header = this.accordions[i];
          panel.setAttribute('role', 'region');
          if (header) panel.setAttribute('aria-labelledby', header.id);
          panel.removeAttribute('tabindex');

          const open = i === this.openIndex && !this.unavailable.has(i);
          panel.toggleAttribute('hidden', !open);
          if (open) this.notifyRevealed(panel);
        });
      }

      onTabClick(event) {
        this.selectFromUser(this.tabs.indexOf(event.currentTarget), false);
      }

      /**
       * A tab the visitor actually chose, as opposed to the one restored when
       * the mode is re-applied. Carries the choice across to the accordion so
       * that resizing down opens the section they were reading, mirroring what
       * onAccordionClick already does in the other direction. Doing this inside
       * select() would not work: renderTabs() calls it on every mode change and
       * would overwrite the accordion's own "everything closed" resting state.
       * @param {number} index
       * @param {boolean} moveFocus
       */
      selectFromUser(index, moveFocus) {
        this.openIndex = index;
        this.select(index, moveFocus);
      }

      onAccordionClick(event) {
        const index = this.accordions.indexOf(event.currentTarget);
        // Toggle: clicking the open row closes it and leaves everything closed.
        this.openIndex = this.openIndex === index ? -1 : index;
        // Keep the tab strip pointing at the last row touched, so resizing up
        // to desktop lands on the section the visitor was actually reading.
        if (this.openIndex > -1) this.activeIndex = this.openIndex;
        this.renderAccordion();
      }

      onBlockSelect(event) {
        const index = this.panels.indexOf(event.target);
        if (index < 0) return;
        this.activeIndex = index;
        this.openIndex = index;
        this.applyMode();
      }

      onKeyDown(event) {
        const current = this.tabs.indexOf(event.currentTarget);
        const available = this.tabs.map((tab, i) => i).filter((i) => !this.unavailable.has(i));
        if (available.length === 0) return;
        const position = Math.max(available.indexOf(current), 0);
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = available[(position + 1) % available.length];
            break;
          case 'ArrowLeft':
            next = available[(position - 1 + available.length) % available.length];
            break;
          case 'Home':
            next = available[0];
            break;
          case 'End':
            next = available[available.length - 1];
            break;
          default:
            return;
        }

        event.preventDefault();
        this.selectFromUser(next, true);
      }

      /**
       * Third-party widgets inside a panel measure themselves on init. A panel
       * that starts `hidden` is display:none, so anything laid out then reads
       * zero — Judge.me's widget is the known case here. Nudge once, the first
       * time a panel actually becomes visible.
       * @param {HTMLElement} panel
       */
      notifyRevealed(panel) {
        if (panel.dataset.revealed === 'true') return;
        panel.dataset.revealed = 'true';
        window.dispatchEvent(new Event('resize'));
        if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
          window.jdgm.customizeBadges();
        }
      }

      /**
       * @param {number} index - tab to activate
       * @param {boolean} moveFocus - true for keyboard activation, false for pointer
       */
      select(index, moveFocus) {
        this.activeIndex = index;

        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          // Roving tabindex: only the active tab is in the tab order.
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.panels.forEach((panel, i) => {
          panel.toggleAttribute('hidden', i !== index);
        });

        if (this.panels[index]) this.notifyRevealed(this.panels[index]);
        if (moveFocus) this.tabs[index].focus();
      }
    },
  );
}
